import { Card, CardHeader, CardContent } from '../../../components/Card';
import { Badge } from '../../../components/Badge';
import { Button } from '../../../components/Button';
import { useFinanceStore } from '../../../store/financeStore';
import { useEffect } from 'react';
import { exportRows, ExportColumn } from '../../../lib/export';
import toast from 'react-hot-toast';

export function ProjectFinance() {
    const { summary, projects, fetchFinanceData, loading } = useFinanceStore();

    useEffect(() => {
        fetchFinanceData();
    }, []);

    const handleExportReport = () => {
        if (projects.length === 0) { toast.error('No financial data to export'); return; }
        const columns: ExportColumn<typeof projects[number]>[] = [
            { header: 'Project', value: (p) => p.name },
            { header: 'Client', value: (p) => p.clientName },
            { header: 'Budget', value: (p) => p.budget },
            { header: 'Estimated Cost', value: (p) => p.cost },
            { header: 'Margin (GP%)', value: (p) => `${p.margin.toFixed(1)}%` },
            { header: 'Status', value: (p) => p.status },
        ];
        const n = exportRows(projects, columns, 'csv', 'project-finance-report');
        toast.success(`Exported ${n} project${n === 1 ? '' : 's'}`);
    };

    const handleGenerateInvoice = () => {
        // No invoicing engine exists yet, so generate a real invoice draft (CSV)
        // from live project financials rather than pretending a backend ran.
        const billable = projects.filter((p) => p.budget > 0);
        if (billable.length === 0) { toast.error('No billable projects to invoice'); return; }
        const columns: ExportColumn<typeof billable[number]>[] = [
            { header: 'Project', value: (p) => p.name },
            { header: 'Client', value: (p) => p.clientName },
            { header: 'Amount Due', value: (p) => p.budget },
            { header: 'Est. Cost', value: (p) => p.cost },
            { header: 'Margin (GP%)', value: (p) => `${p.margin.toFixed(1)}%` },
        ];
        const n = exportRows(billable, columns, 'csv', 'invoice-draft');
        toast.success(`Invoice draft generated for ${n} project${n === 1 ? '' : 's'}`);
    };

    return (
        <div className="space-y-6">
            <div className="flex justify-between items-center">
                <div>
                    <h1 className="tracking-tight text-foreground font-display text-page-title">Project <span className="text-foreground">Finance</span></h1>
                    <p className="text-muted-foreground mt-1">Financial tracking, invoicing, and profitability (GP%)</p>
                </div>
                <div className="flex gap-3">
                    <Button variant="secondary" onClick={handleExportReport} disabled={loading}>Export Report</Button>
                    <Button variant="primary" onClick={handleGenerateInvoice} disabled={loading}>Generate Invoice</Button>
                </div>
            </div>

            <div className="grid grid-cols-2 lg:grid-cols-4 gap-6">
                {[
                    { label: 'Total Portfolio Revenue', value: summary ? `$${(summary.totalRevenue / 1000).toFixed(1)}k` : '$0', icon: '', color: 'text-success' },
                    { label: 'Monthly Run Rate', value: summary ? `$${(summary.monthlyRunRate / 1000).toFixed(1)}k` : '$0', icon: '', color: 'text-primary' },
                    { label: 'Average Gross Profit', value: summary ? `${summary.averageMargin.toFixed(1)}%` : '0%', icon: '', color: 'text-warning' },
                    { label: 'Overdue Invoices', value: summary ? `$${summary.overdueInvoices}` : '$0', icon: '', color: 'text-destructive' }
                ].map((stat, i) => (
                    <Card key={i} className="hover:-translate-y-1 transition-transform">
                        <CardContent className="p-6">
                            <div className="flex justify-between items-start">
                                <div>
                                    <p className="text-xs text-muted-foreground font-semibold mb-1">{stat.label}</p>
                                    <p className={`text-3xl font-semibold ${stat.color}`}>{stat.value}</p>
                                </div>
                                <span className="text-2xl opacity-40">{stat.icon}</span>
                            </div>
                        </CardContent>
                    </Card>
                ))}
            </div>

            <Card>
                <CardHeader className="bg-card/30 px-6 py-4">
                    <h2 className="text-foreground font-display text-section-title">Project Financials</h2>
                </CardHeader>
                <CardContent className="p-0">
                    <table className="w-full text-sm">
                        <thead>
                            <tr className="text-sm text-muted-foreground font-semibold bg-card/50 border-b border-border">
                                <th className="text-left px-6 py-4">Project</th>
                                <th className="text-right px-4 py-4">Budget</th>
                                <th className="text-right px-4 py-4">Est. Cost</th>
                                <th className="text-right px-4 py-4">Margin (GP%)</th>
                                <th className="text-center px-4 py-4">Status</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-border-12">
                            {projects.map(p => {
                                return (
                                    <tr key={p.id} className="hover:bg-card/30 transition-colors">
                                        <td className="px-6 py-4">
                                            <p className="font-semibold text-foreground">{p.name}</p>
                                            <p className="text-xs text-muted-foreground">{p.clientName}</p>
                                        </td>
                                        <td className="px-4 py-4 text-right font-mono text-foreground">${p.budget.toLocaleString()}</td>
                                        <td className="px-4 py-4 text-right font-mono text-warning">${p.cost.toLocaleString()}</td>
                                        <td className="px-4 py-4 text-right">
                                            <span className={`font-semibold ${p.margin >= 40 ? 'text-success' : p.margin >= 20 ? 'text-warning' : 'text-destructive'}`}>
                                                {p.margin.toFixed(1)}%
                                            </span>
                                        </td>
                                        <td className="px-4 py-4 text-center">
                                            <Badge variant={p.status === 'active' ? 'success' : 'default'}>{p.status}</Badge>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </CardContent>
            </Card>
        </div>
    );
}
